import axios from 'axios'

/**
 * GET through the app's axios instance so the global 401 interceptor keeps
 * working, forwarding the query's abort signal so an unmounted or superseded
 * query cancels its request. Resolves to the response body.
 *
 * @param {string} url
 * @param {{ params?: Record<string, unknown>, signal?: AbortSignal } & Record<string, unknown>} [options]
 */
export const apiGet = async (url, { params, signal, ...config } = {}) => {
  const { data } = await axios.get(url, { params, signal, ...config })
  return data
}

/**
 * GET that resolves to `{ data, headers }` instead of the body alone, for
 * queryFns that read response headers (Insights freshness metadata). Same axios
 * instance as `apiGet`, so the 401 interceptor still applies.
 *
 * @param {string} url
 * @param {{ params?: Record<string, unknown>, signal?: AbortSignal } & Record<string, unknown>} [options]
 * @returns {Promise<{ data: any, headers: Record<string, string> }>}
 */
export const apiGetResponse = async (url, { params, signal, ...config } = {}) => {
  const { data, headers } = await axios.get(url, { params, signal, ...config })
  return { data, headers }
}

/** POST counterpart of `apiGetResponse`: resolves to `{ data, headers }`. */
export const apiPostResponse = async (url, body, config = {}) => {
  const { data, headers } = await axios.post(url, body, config)
  return { data, headers }
}

/**
 * Mutating requests through the same axios instance. Each resolves to the
 * response body. Use them as `mutationFn`s so the 401 interceptor and the
 * app's error normalization keep working.
 */
export const apiPost = async (url, body, config = {}) => {
  const { data } = await axios.post(url, body, config)
  return data
}

export const apiPut = async (url, body, config = {}) => {
  const { data } = await axios.put(url, body, config)
  return data
}

export const apiPatch = async (url, body, config = {}) => {
  const { data } = await axios.patch(url, body, config)
  return data
}

export const apiDelete = async (url, config = {}) => {
  const { data } = await axios.delete(url, config)
  return data
}
